import { useAppSelector } from '@app/hooks';
import { RootState } from '@app/store';
import {
  useGetContainerVersionsQuery,
  useRequestVersionReportMutation,
  useTriggerContainerUpdateMutation,
} from '@features/home/api';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import RefreshIcon from '@mui/icons-material/Refresh';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import WarningIcon from '@mui/icons-material/Warning';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  IconButton,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from '@mui/material';
import { formatDate } from '@lib/utils';
import { useCallback, useMemo, useState } from 'react';

const CONTAINER_LABELS: Record<string, string> = {
  homeassistant: 'HomeAssistant',
  kiosk: 'Kiosk (HUI App)',
  scene_engine: 'Scene Engine',
  trigger_engine: 'Trigger Engine',
  docrepo: 'Document Repo',
  zwavejs2mqtt: 'Z-Wave JS',
};

const GatewayVersions = () => {
  const property = useAppSelector((state: RootState) => state.property);
  const userTenant = useAppSelector((state: RootState) => state.userTenant);
  const tenant = useAppSelector((state: RootState) => state.tenant);

  const currentTenant = useMemo(
    () => tenant.currentTenant || userTenant,
    [tenant.currentTenant, userTenant]
  );

  const [updateTriggered, setUpdateTriggered] = useState(false);

  const {
    data: versionData,
    isLoading,
    refetch,
  } = useGetContainerVersionsQuery(
    {
      userTenantId: currentTenant?.tenant_id || '',
      propertyId: property?.property_id || '',
    },
    {
      skip: !currentTenant?.tenant_id || !property?.property_id,
    }
  );

  const [triggerUpdate, { isLoading: isUpdating }] =
    useTriggerContainerUpdateMutation();

  const [requestVersionReport, { isLoading: isRequesting }] =
    useRequestVersionReportMutation();

  const handleUpdate = useCallback(async () => {
    if (!currentTenant?.tenant_id || !property?.property_id) return;
    await triggerUpdate({
      userTenantId: currentTenant.tenant_id,
      propertyId: property.property_id,
    });
    setUpdateTriggered(true);
  }, [currentTenant?.tenant_id, property?.property_id, triggerUpdate]);

  const handleRefreshVersions = useCallback(async () => {
    if (!currentTenant?.tenant_id || !property?.property_id) return;
    await requestVersionReport({
      userTenantId: currentTenant.tenant_id,
      propertyId: property.property_id,
    });
    // Wait a few seconds for the gateway to respond, then refetch
    setTimeout(() => refetch(), 5000);
  }, [
    currentTenant?.tenant_id,
    property?.property_id,
    requestVersionReport,
    refetch,
  ]);

  const versions = versionData?.versions;
  const lastUpdated = versionData?.updated;

  const hasUpdatesAvailable = useMemo(() => {
    if (!versions) return false;
    return Object.values(versions).some(
      (v) => v.current && v.latest && v.current !== v.latest
    );
  }, [versions]);

  if (!property?.property_id) return null;

  return (
    <Stack spacing={2} sx={{ width: 'inherit' }}>
      <Stack direction="row" justifyContent="space-between" alignItems="center">
        <Typography variant="h5">Gateway Software</Typography>
        <Stack direction="row" spacing={1}>
          <Tooltip title="Request version report from gateway">
            <IconButton
              onClick={handleRefreshVersions}
              disabled={isRequesting}
              size="small"
            >
              {isRequesting ? (
                <CircularProgress size={20} />
              ) : (
                <RefreshIcon />
              )}
            </IconButton>
          </Tooltip>
          <Button
            variant="outlined"
            size="small"
            startIcon={
              isUpdating ? (
                <CircularProgress size={16} />
              ) : (
                <SystemUpdateAltIcon />
              )
            }
            onClick={handleUpdate}
            disabled={isUpdating || updateTriggered}
          >
            {updateTriggered ? 'Update Sent' : 'Update Containers'}
          </Button>
        </Stack>
      </Stack>

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : !versions ? (
        <Alert severity="info" variant="outlined" sx={{ background: 'white' }}>
          No version data available. Click the refresh button to request a
          version report from the gateway.
        </Alert>
      ) : (
        <>
          <TableContainer>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Container</TableCell>
                  <TableCell>Current Version</TableCell>
                  <TableCell>Latest Available</TableCell>
                  <TableCell align="center">Status</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {Object.entries(versions).map(([name, info]) => {
                  const isUpToDate =
                    info.current && info.latest && info.current === info.latest;
                  const hasUpdate =
                    info.current && info.latest && info.current !== info.latest;

                  return (
                    <TableRow key={name}>
                      <TableCell>
                        {CONTAINER_LABELS[name] || name}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {info.current || '—'}
                      </TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.85rem' }}>
                        {info.latest || '—'}
                      </TableCell>
                      <TableCell align="center">
                        {isUpToDate ? (
                          <Tooltip title="Up to date">
                            <CheckCircleIcon
                              color="success"
                              fontSize="small"
                            />
                          </Tooltip>
                        ) : hasUpdate ? (
                          <Tooltip title="Update available">
                            <WarningIcon color="warning" fontSize="small" />
                          </Tooltip>
                        ) : (
                          '—'
                        )}
                      </TableCell>
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </TableContainer>
          {lastUpdated && (
            <Typography variant="caption" color="text.secondary">
              Last reported {formatDate(lastUpdated).fromNow()}
            </Typography>
          )}
          {hasUpdatesAvailable && !updateTriggered && (
            <Alert severity="warning" variant="outlined" sx={{ background: 'white' }}>
              Some containers have updates available. Click "Update Containers"
              to trigger an update.
            </Alert>
          )}
          {updateTriggered && (
            <Alert severity="info" variant="outlined" sx={{ background: 'white' }}>
              Update command sent to gateway. It may take several minutes for
              all containers to update. Refresh versions to check progress.
            </Alert>
          )}
        </>
      )}
    </Stack>
  );
};

export default GatewayVersions;
